import React, { useState, useEffect, useRef } from "react";
import * as XLSX from 'xlsx';
import { connect } from "react-redux";
import { 
  DatePicker, 
  Select, 
  Col, 
  Row, 
  Spin, 
  Divider, 
  AutoComplete, 
  Button, 
  Table, 
  Upload, 
  message, 
  Modal,
  InputNumber,
  Tag,
  Card
} from "antd";
import { 
  UploadOutlined, 
  DownloadOutlined, 
  CheckCircleOutlined, 
  CloseCircleOutlined 
} from "@ant-design/icons";
import { importBulkPromotionExcel, fetchPromotionHistory } from "../../../../actions/employeeSalaryActions";
import moment from "moment";

const { Option } = Select;
const SalaryIncreament = ({ auth, importBulkPromotionExcel, fetchPromotionHistory }) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);
  const [incrementType, setIncrementType] = useState("PERCENTAGE");
  const [incrementAmount, setIncrementAmount] = useState(null);
  const [incrementMonth, setIncrementMonth] = useState(null);
  const [resultModal, setResultModal] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  const pageno = useRef(1);
  const page_size = useRef(10);
  const keyword = useRef("");

  useEffect(() => {
    loadPromotionHistory();
  }, []);

  const loadPromotionHistory = async () => {
    try {
      setLoading(true);
      const result = await fetchPromotionHistory();
      setData(result.results || result);
    } catch (error) {
      message.error("Failed to load promotion history");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    try {
      setLoading(true);
      const filters = {};
      
      if (keyword.current) {
        filters.employee = keyword.current;
      }
      
      const result = await fetchPromotionHistory(filters);
      setData(result.results || result);
    } catch (error) {
      message.error("Search failed");
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (file) => {
    setUploadFile(file);
    return false; // Prevent auto upload
  };

  const handleSubmit = async () => {
    // Validation
    if (!uploadFile) {
      message.error("Please upload an Excel file");
      return;
    }
    
    if (!incrementAmount || incrementAmount <= 0) {
      message.error("Please enter a valid increment amount");
      return;
    }
    
    if (!incrementMonth) {
      message.error("Please select increment month");
      return;
    }

    try {
      setLoading(true);
      
      const promotionData = {
        increment_type: incrementType,
        increamentAmount: incrementAmount,
        increamentMonth: moment(incrementMonth).format("YYYY-MM-DD")
      };

      const result = await importBulkPromotionExcel(uploadFile, promotionData);
      
      setImportResult(result);
      setResultModal(true);
      
      if (result.success_count > 0) {
        message.success(`Successfully processed ${result.success_count} promotions`);
        // Reset form
        setUploadFile(null);
        setIncrementAmount(null);
        setIncrementMonth(null);
        // Reload data
        loadPromotionHistory();
      }
      
      if (result.failed_count > 0) {
        message.warning(`${result.failed_count} records failed to process`);
      }
      
    } catch (error) {
      const errorMsg = error.response && error.response.data && error.response.data.error 
        ? error.response.data.error 
        : error.message;
      message.error("Failed to import promotions: " + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    // Create Excel template with proper formatting
    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const wsData = [
      ['employee_pin'],  // Header row
      ['12345'],         // Example row 1
      ['67890'],         // Example row 2
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Employees');
    
    // Generate Excel file and trigger download
    XLSX.writeFile(wb, 'bulk_promotion_template.xlsx');
  };

  const columns = [
    {
      title: "Employee",
      dataIndex: "employee_name",
      key: "employee_name",
    },
    {
      title: "Designation Change",
      dataIndex: "designation_change",
      key: "designation_change",
    },
    {
      title: "Type",
      dataIndex: "increment_type",
      key: "increment_type",
      render: (type) => (
        <Tag color={type === "PERCENTAGE" ? "blue" : "green"}>
          {type}
        </Tag>
      ),
    },
    {
      title: "Increment",
      dataIndex: "increment_display",
      key: "increment_display",
      render: (text, record) => (
        <Tag color={record.increament ? "success" : "error"}>
          {text}
        </Tag>
      ),
    },
    {
      title: "Amount Change",
      dataIndex: "amount_display",
      key: "amount_display",
      render: (text, record) => (
        <span style={{ 
          color: record.increament ? "green" : "red",
          fontWeight: "bold" 
        }}>
          {text}
        </span>
      ),
    },
    {
      title: "Effective Month",
      dataIndex: "increamentMonth",
      key: "increamentMonth",
      render: (date) => moment(date).format("MMM YYYY"),
    },
    {
      title: "Created",
      dataIndex: "created",
      key: "created",
      render: (date) => moment(date).format("DD MMM YYYY HH:mm"),
    },
  ];

  const failedRecordsColumns = [
    {
      title: "Row",
      dataIndex: "row",
      key: "row",
    },
    {
      title: "Employee PIN",
      dataIndex: "employee_pin",
      key: "employee_pin",
    },
    {
      title: "Error",
      dataIndex: "error",
      key: "error",
      render: (text) => <span style={{ color: "red" }}>{text}</span>,
    },
  ];

  return (
    <>
      <div className="site-layout-background main-frame">
        <Card title="Bulk Salary Promotion Import" style={{ marginBottom: 16 }}>
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <div>Increment Type:</div>
              <Select
                value={incrementType}
                onChange={setIncrementType}
                style={{ width: "100%" }}
              >
                <Option value="PERCENTAGE">Percentage</Option>
                <Option value="DECIMAL">Decimal Amount</Option>
              </Select>
            </Col>
            
            <Col span={6}>
              <div>Increment Amount:</div>
              <InputNumber
                value={incrementAmount}
                onChange={setIncrementAmount}
                style={{ width: "100%" }}
                placeholder={incrementType === "PERCENTAGE" ? "e.g., 10 for 10%" : "e.g., 5000"}
                min={0}
                max={incrementType === "PERCENTAGE" ? 100 : undefined}
              />
            </Col>
            
            <Col span={6}>
              <div>Effective Month:</div>
              <DatePicker
                value={incrementMonth}
                onChange={setIncrementMonth}
                picker="month"
                style={{ width: "100%" }}
                placeholder="Select Month"
              />
            </Col>
            
            <Col span={6}>
              <div>Excel File:</div>
              <Upload
                beforeUpload={handleFileUpload}
                onRemove={() => setUploadFile(null)}
                maxCount={1}
                accept=".xlsx,.xls,.csv"
              >
                <Button icon={<UploadOutlined />} style={{ width: "100%" }}>
                  {uploadFile ? "Change File" : "Upload Excel"}
                </Button>
              </Upload>
            </Col>
          </Row>
          
          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col span={12}>
              <Button
                type="dashed"
                icon={<DownloadOutlined />}
                onClick={downloadTemplate}
                style={{ width: "100%" }}
              >
                Download Template
              </Button>
            </Col>
            <Col span={12}>
              <Button
                type="primary"
                onClick={handleSubmit}
                loading={loading}
                disabled={!uploadFile || !incrementAmount || !incrementMonth}
                style={{ width: "100%" }}
              >
                Submit Bulk Promotion
              </Button>
            </Col>
          </Row>
        </Card>

        <Card title="Promotion History">
            <Row wrap={false} gutter={[16, 16]}>
                {/* <Col flex="auto">
                keyword:
                <AutoComplete
                    placeholder="EPIN / Name / Phone"
                    onChange={(value) => {
                    keyword.current = value;
                    }}
                    style={{ width: "100%", minWidth: "140px" }}
                />
                </Col>
                <Col flex="auto">
                <div style={{ visibility: "hidden" }}>.</div>
                <Button style={{ width: "100%" }} onClick={handleSearch}>
                    Search
                </Button>
                </Col> */}
                <Col flex="auto">
                <div style={{ visibility: "hidden" }}>.</div>
                <Button style={{ width: "100%" }} onClick={loadPromotionHistory}>
                    Refresh
                </Button>
                </Col>
            </Row>
          
          <Divider />
          
          <Spin spinning={loading}>
            <Table
              columns={columns}
              dataSource={data}
              rowKey="id"
              pagination={{
                pageSize: page_size.current,
                current: pageno.current,
              }}
            />
          </Spin>
        </Card>
      </div>

      <Modal
        title="Import Results"
        visible={resultModal}
        onOk={() => setResultModal(false)}
        onCancel={() => setResultModal(false)}
        width={800}
        footer={[
          <Button key="close" type="primary" onClick={() => setResultModal(false)}>
            Close
          </Button>,
        ]}
      >
        {importResult && (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <Card>
                  <div style={{ textAlign: "center" }}>
                    <div style={{ fontSize: 24, fontWeight: "bold" }}>
                      {importResult.total_records}
                    </div>
                    <div>Total Records</div>
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <div style={{ textAlign: "center" }}>
                    <CheckCircleOutlined
                      style={{ fontSize: 24, color: "green" }}
                    />
                    <div style={{ fontSize: 24, fontWeight: "bold", color: "green" }}>
                      {importResult.success_count}
                    </div>
                    <div>Successful</div>
                  </div>
                </Card>
              </Col>
              <Col span={8}>
                <Card>
                  <div style={{ textAlign: "center" }}>
                    <CloseCircleOutlined
                      style={{ fontSize: 24, color: "red" }}
                    />
                    <div style={{ fontSize: 24, fontWeight: "bold", color: "red" }}>
                      {importResult.failed_count}
                    </div>
                    <div>Failed</div>
                  </div>
                </Card>
              </Col>
            </Row>

            {importResult.failed_records && importResult.failed_records.length > 0 && (
              <>
                <Divider>Failed Records</Divider>
                <Table
                  columns={failedRecordsColumns}
                  dataSource={importResult.failed_records}
                  pagination={false}
                  size="small"
                  scroll={{ y: 300 }}
                />
              </>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
  };
};

export default connect(mapStateToProps, {
  importBulkPromotionExcel,
  fetchPromotionHistory,
})(SalaryIncreament);
